/**
 * BOM Explorer — multi-level component breakdown for a manufactured product.
 *
 * Layout:
 *   - product Select at top
 *   - tree body: recursive `BomTreeNode` rendering each component's qty,
 *     standard cost, and on-hand stock totals.
 *   - aggregated cost summary card on the right (totals across the tree)
 *
 * Pure HTML/SVG — no chart libs. Tree uses chevron-toggle nodes.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Boxes, Package2 } from "lucide-react";

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@bizop/ui-kit";
import { formatMoney } from "@bizop/shared/currency";

import { PageHeader } from "../../../components/PageHeader";
import { BomTreeNode } from "../../../components/production/BomTreeNode";
import { calculateBomEconomics } from "../../../components/production/EconomicsCalculator";
import { useHasPermission } from "../../../lib/permissions";
import {
  mockBomLines,
  mockBoms,
  mockProducts,
  mockUnits,
  type MockBom,
  type MockBomLine,
  type MockProduct,
  type MockUnit,
} from "../../../lib/catalogMock";
import { mockStockMovements, type MockStockMovement } from "../../../lib/inventoryMock";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";

export const Route = createFileRoute("/_app/production/bom-explorer")({
  component: BomExplorerPage,
});

function BomExplorerPage() {
  const canSeeFinance = useHasPermission("finance.read");
  const [products] = usePersistentMockState<MockProduct[]>("products", mockProducts);
  const [boms] = usePersistentMockState<MockBom[]>("boms", mockBoms);
  const [bomLines] = usePersistentMockState<MockBomLine[]>("bomLines", mockBomLines);
  const [units] = usePersistentMockState<MockUnit[]>("units", mockUnits);
  const [stockMovements] = usePersistentMockState<MockStockMovement[]>(
    "stockMovements",
    mockStockMovements,
  );

  const manufactured = useMemo(
    () =>
      products.filter(
        (p) => p.productKind === "manufactured" && p.isActive,
      ),
    [products],
  );
  const [productId, setProductId] = useState<string>(
    manufactured[0]?._id ?? "",
  );

  const product = manufactured.find((p) => p._id === productId);
  const activeBom = useMemo(
    () =>
      boms.find(
        (b) => b.productId === productId && b.isActive,
      ) ?? null,
    [boms, productId],
  );
  const directLines = useMemo(
    () =>
      activeBom
        ? bomLines
            .filter((l) => l.bomId === activeBom._id)
            .sort((a, b) => a.sortOrder - b.sortOrder)
        : [],
    [activeBom, bomLines],
  );

  /* Lookup tables for the tree. */
  const unitCodeById = useMemo(
    () => new Map(units.map((u) => [u._id, u.code])),
    [units],
  );

  /* On-hand totals derived from the inventory mock (sum across warehouses). */
  const onHandByProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of stockMovements) {
      const cur = map.get(m.productId) ?? 0;
      map.set(m.productId, cur + m.quantityDelta);
    }
    return map;
  }, [stockMovements]);

  /* Compute aggregate roll-up cost = sum of (component qty × standard cost),
     walked recursively through sub-assemblies. */
  const rollup = useMemo(() => {
    if (!product || !activeBom) {
      return { totalCostCents: 0n, leafCount: 0, depth: 0 };
    }
    let totalCents = 0n;
    let leafCount = 0;
    let maxDepth = 0;
    function walk(pid: string, multiplier: number, depth: number) {
      maxDepth = Math.max(maxDepth, depth);
      const childBom = boms.find(
        (b) => b.productId === pid && b.isActive,
      );
      const lines = childBom
        ? bomLines.filter((l) => l.bomId === childBom._id)
        : [];
      if (lines.length === 0) {
        // Leaf: count its standard cost.
        const p = products.find((pp) => pp._id === pid);
        if (p) {
          totalCents +=
            p.standardCostCents *
            BigInt(Math.max(1, Math.round(multiplier)));
          leafCount += 1;
        }
        return;
      }
      for (const l of lines) {
        walk(
          l.componentProductId,
          l.quantity * Math.max(1, multiplier),
          depth + 1,
        );
      }
    }
    for (const l of directLines) {
      walk(l.componentProductId, l.quantity, 1);
    }
    return {
      totalCostCents: totalCents,
      leafCount,
      depth: maxDepth,
    };
  }, [product, activeBom, directLines, boms, bomLines, products]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="BOM Explorer"
        description="Drill into a product's full multi-level Bill of Materials. Click any sub-assembly to expand its components."
      />

      {/* Top — product picker */}
      <section className="flex flex-wrap items-center gap-3">
        <Select value={productId} onValueChange={setProductId}>
          <SelectTrigger className="w-96">
            <SelectValue placeholder="Pick a manufactured product" />
          </SelectTrigger>
          <SelectContent>
            {manufactured.map((p) => (
              <SelectItem key={p._id} value={p._id}>
                {p.displayName}
                <span className="ml-2 text-(--text-tertiary)">· {p.sku}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {activeBom ? (
          <Badge variant="info">{activeBom.versionLabel}</Badge>
        ) : product ? (
          <Badge variant="warning">No active BOM</Badge>
        ) : null}
      </section>

      {/* Body */}
      {product && activeBom ? (
        <>
          <section className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
            {/* Tree */}
            <div className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-2 min-h-[320px]">
              <BomTreeNode
                productId={product._id}
                quantity={activeBom.outputQuantity}
                unitCode={unitCodeById.get(activeBom.outputUnitId) ?? "pcs"}
                depth={0}
                unitCodeById={unitCodeById}
                onHandByProduct={onHandByProduct}
              />
            </div>

            {/* Summary card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[13px] flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-(--accent)" />
                  Roll-up
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <Stat
                  label="Direct components"
                  value={String(directLines.length)}
                />
                <Stat
                  label="Leaf parts"
                  value={String(rollup.leafCount)}
                />
                <Stat
                  label="Tree depth"
                  value={String(rollup.depth)}
                  hint="Longest chain from finished product to a leaf"
                />
                <Stat
                  label="Standard cost (rolled-up)"
                  value={
                    canSeeFinance
                      ? formatMoney(rollup.totalCostCents, "BDT", {
                          locale: "en-BD",
                        })
                      : "—"
                  }
                  hint="Sum of leaf parts × required quantity"
                />
                {activeBom.notes ? (
                  <div className="mt-2 rounded-[var(--radius-sm)] bg-(--bg-canvas) p-2 text-[12px] text-(--text-secondary)">
                    {activeBom.notes}
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </section>

          {/* Phase 9: BOM-level economics — per-unit cost, sale price,
              margin and margin %. Same calculator the work-order
              economics card uses, so the two views are consistent. */}
          <BomEconomicsPanel
            product={product}
            componentCostPerUnitCents={rollup.totalCostCents}
            canSeeFinance={canSeeFinance}
          />
        </>
      ) : product ? (
        <section className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) p-8 text-center text-(--text-tertiary)">
          <Package2 className="h-6 w-6 mx-auto mb-2" />
          <p className="text-[13px]">
            {product.displayName} doesn't have an active BOM yet.
          </p>
          <p className="text-[12px]">
            Create one on the BOM page before exploring.
          </p>
        </section>
      ) : (
        <section className="rounded-[var(--radius-md)] border border-dashed border-(--border-subtle) p-8 text-center text-(--text-tertiary)">
          <p className="text-[13px]">
            Pick a manufactured product to view its BOM tree.
          </p>
        </section>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <span className="text-[14px] font-mono tabular-nums text-(--text-primary)">
        {value}
      </span>
      {hint ? (
        <span className="text-[11px] text-(--text-tertiary)">{hint}</span>
      ) : null}
    </div>
  );
}

/**
 * BOM-level economics: per-unit cost vs. standard sale price + margin.
 * This is the BOM-explorer counterpart to `ProductionEconomics` (which
 * works on a specific work order). Shared math via `calculateBomEconomics`.
 */
function BomEconomicsPanel({
  product,
  componentCostPerUnitCents,
  canSeeFinance,
}: {
  product: MockProduct;
  componentCostPerUnitCents: bigint;
  canSeeFinance: boolean;
}) {
  const econ = calculateBomEconomics(product, componentCostPerUnitCents);

  // marginPercentE4 = (margin / sale) * 1e6, so /1e4 = ×100 with 2 decimal precision.
  const marginPctDisplay =
    econ.salePricePerUnitCents > 0n
      ? `${(Number(econ.marginPercentE4) / 10_000).toFixed(1)}%`
      : "—";

  const isLoss = econ.marginPerUnitCents < 0n;
  const moneyOpts = { locale: "en-BD" } as const;

  return (
    <section
      className={cn(
        "grid grid-cols-2 lg:grid-cols-4 gap-3 rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-4",
      )}
      aria-label="BOM economics"
    >
      <EconomicsStat
        label="Component cost / unit"
        value={
          canSeeFinance
            ? formatMoney(componentCostPerUnitCents, "BDT", moneyOpts)
            : "—"
        }
        sub="Sum of leaf parts at standard cost"
      />
      <EconomicsStat
        label="Standard sale price"
        value={
          canSeeFinance
            ? formatMoney(econ.salePricePerUnitCents, "BDT", moneyOpts)
            : "—"
        }
        sub="Standard 2026 list (×1.60)"
      />
      <EconomicsStat
        label="Margin / unit"
        value={
          canSeeFinance
            ? formatMoney(econ.marginPerUnitCents, "BDT", moneyOpts)
            : "—"
        }
        sub={isLoss ? "Loss-making at this price" : "Sale − component cost"}
        tone={
          !canSeeFinance ? "neutral" : isLoss ? "danger" : "success"
        }
      />
      <EconomicsStat
        label="Margin %"
        value={canSeeFinance ? marginPctDisplay : "—"}
        sub="Of the sale price"
        tone={
          !canSeeFinance
            ? "neutral"
            : isLoss
              ? "danger"
              : econ.marginPercentE4 < 200_000n
                ? "warning"
                : "success"
        }
      />
    </section>
  );
}

function EconomicsStat({
  label,
  value,
  sub,
  tone = "neutral",
}: {
  label: string;
  value: string;
  sub: string;
  tone?: "neutral" | "success" | "warning" | "danger";
}) {
  const TONE: Record<string, string> = {
    neutral: "text-(--text-primary)",
    success: "text-(--success)",
    warning: "text-(--warning)",
    danger: "text-(--danger)",
  };
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-(--text-tertiary) font-medium">
        {label}
      </span>
      <span
        className={cn(
          "font-mono tabular-nums text-[18px] leading-tight",
          TONE[tone],
        )}
      >
        {value}
      </span>
      <span className="text-[11px] text-(--text-secondary) leading-snug">
        {sub}
      </span>
    </div>
  );
}
